/**
 * Location label helpers — render a location as `Name [ABBR City ±HH:MM]`
 * with the bracketed timezone meta visually de-emphasised.
 *
 * Bracketed segments:
 *   - ABBR  — short tz abbreviation (DST-aware) via `Intl.DateTimeFormat({ timeZoneName: 'short' })`
 *   - City  — friendly name derived from the IANA id (`America/New_York` → `New York`)
 *   - ±HH:MM — UTC offset via shared `computeOffsetString` helper
 * (all three built by the shared `formatTzMeta`).
 */
import React from 'react';
import { formatTzMeta } from './timeFormatters.js';

/**
 * Plain-string variant for Autocomplete `textValue` (keyboard search) and
 * other non-JSX contexts. Returns e.g. `New York [EDT New York -04:00]`.
 *
 * @param {string} locationName
 * @param {string} timeZoneId IANA id (e.g. `America/New_York`)
 * @param {Date} [date] Defaults to `new Date()` — pass the event's start time
 *                      for DST-correct abbr/offset in historical contexts.
 */
export function formatLocationLabelString(locationName, timeZoneId, date) {
	const name = locationName || '';
	if (!timeZoneId) return name;
	const meta = formatTzMeta(date instanceof Date ? date : new Date(), timeZoneId);
	return meta ? `${name} [${meta}]` : name;
}

/**
 * JSX variant with a dimmed bracket span. Use inside Field values and
 * AutocompleteItem children.
 */
export function LocationLabel({ name, timeZoneId, date }) {
	if (!timeZoneId) return <>{name}</>;
	const meta = formatTzMeta(date instanceof Date ? date : new Date(), timeZoneId);
	return (
		<>
			<span>{name}</span>
			{meta && (
				<>
					{' '}
					<span className="text-default-400 text-xs">[{meta}]</span>
				</>
			)}
		</>
	);
}
